/**
 * Confidentiality / PHI disclaimer API client.
 *
 * Pairs with backend endpoints defined in accounts/views_disclaimer.py:
 *   GET  /auth/disclaimer/platform-banner/  (public)
 *   GET  /auth/disclaimer/current/          (authenticated, hospital-scoped)
 *   POST /auth/disclaimer/accept/           (authenticated, hospital-scoped)
 *
 * Also exposes a tiny pub/sub for the "the backend just told us the disclaimer
 * is required" signal: when an arbitrary API call returns 412 with
 * `code=disclaimer_required`, the axios interceptor calls
 * `notifyDisclaimerRequired(...)` and `DisclaimerGate` re-runs its check and
 * surfaces the modal.
 */

import api from './api';

export type ComplianceMode = 'USER_RESPONSIBILITY' | 'FULL_HIPAA' | 'DISABLED';

/** Payload mirrored from the backend `DisclaimerRequired` 412 response. */
export interface DisclaimerRequiredPayload {
  hospital_id: number | null;
  version: number | null;
  compliance_mode: ComplianceMode | null;
}

type Listener = (p: DisclaimerRequiredPayload) => void;
const _listeners = new Set<Listener>();

/** Subscribe to "disclaimer required" events. Returns an unsubscribe fn. */
export function onDisclaimerRequired(listener: Listener): () => void {
  _listeners.add(listener);
  return () => {
    _listeners.delete(listener);
  };
}

/** Called by the api response interceptor when a 412/disclaimer_required is observed. */
export function notifyDisclaimerRequired(payload: DisclaimerRequiredPayload): void {
  _listeners.forEach((fn) => {
    try {
      fn(payload);
    } catch (err) {
      // Listener errors must not break the request pipeline.
      console.warn('[disclaimerService] listener threw:', err);
    }
  });
}

export interface PlatformBannerResponse {
  enabled: boolean;
  banner_text: string;
}

export interface CurrentDisclaimerResponse {
  enabled: boolean;
  /** Selects wording style & visual treatment for the modal. */
  compliance_mode: ComplianceMode;
  version: number | null;
  text: string;
  banner_text: string;
  acceptance_required: boolean;
  hospital_id: number | null;
  hospital_name: string | null;
}

export interface AcceptDisclaimerResponse {
  accepted: true;
  version: number;
  hospital_id: number;
  newly_recorded?: boolean;
  noop?: boolean;
}

export const disclaimerService = {
  /** Public: fetch the short banner shown on the login page (pre-auth). */
  async fetchPlatformBanner(): Promise<PlatformBannerResponse> {
    return api.get<PlatformBannerResponse>('/auth/disclaimer/platform-banner/');
  },

  /** Authenticated: fetch the current hospital's disclaimer and acceptance status. */
  async fetchCurrent(): Promise<CurrentDisclaimerResponse> {
    return api.get<CurrentDisclaimerResponse>('/auth/disclaimer/current/');
  },

  /** Authenticated: record the user's acceptance of the current hospital's disclaimer. */
  async accept(): Promise<AcceptDisclaimerResponse> {
    return api.post<AcceptDisclaimerResponse>('/auth/disclaimer/accept/', {});
  },
};

export default disclaimerService;
